import { useCallback, useEffect, useState } from "react";
import {
  createNewPriceBaseline,
  expireNewPriceBaseline,
  fetchNewPriceBaseline,
  fetchNewPriceBaselines,
  markNewPriceBaselineCurrent,
  recalculateAllNewPriceMatches,
  recalculateBaselineNewPriceMatches,
  updateNewPriceBaseline,
} from "../../../../services/vehicleNewPriceBaselineApi";
import AdminStatePanel from "../layout/AdminStatePanel";
import AdminTableContainer from "../layout/AdminTableContainer";
import { formatCop } from "../../mobility/vehicleMarketFormat";

const EMPTY_FORM = {
  vehicleSourceId: "",
  sourceUrl: "",
  brandDealerName: "",
  make: "",
  model: "",
  trim: "",
  version: "",
  modelYear: new Date().getFullYear(),
  priceCop: "",
  currencyCode: "COP",
  priceType: "MSRP",
  priceIncludesTax: false,
  priceIncludesFees: false,
  availabilityStatus: "AVAILABLE",
  city: "Medellín",
  department: "Antioquia",
  country: "Colombia",
  effectiveDate: new Date().toISOString().slice(0, 10),
  isCurrent: true,
  confidenceScore: 80,
  notes: "",
};

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

const VehicleNewPriceBaselinePage = ({ onClose }) => {
  const [baselines, setBaselines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [toast, setToast] = useState("");
  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      const data = await fetchNewPriceBaselines({ search: search || undefined, limit: 200 });
      setBaselines(data.baselines || []);
    } catch (err) {
      setErrorMessage(err.message || "Unable to load baselines.");
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormOpen(true);
  };

  const openEdit = async (id) => {
    try {
      const data = await fetchNewPriceBaseline(id);
      const b = data.baseline;
      setEditingId(id);
      setForm({
        vehicleSourceId: b.vehicleSourceId || "",
        sourceUrl: b.sourceUrl || "",
        brandDealerName: b.brandDealerName || "",
        make: b.makeRaw || "",
        model: b.modelRaw || "",
        trim: b.trimRaw || "",
        version: b.versionRaw || "",
        modelYear: b.modelYear,
        priceCop: b.priceCop,
        currencyCode: b.currencyCode || "COP",
        priceType: b.priceType || "MSRP",
        priceIncludesTax: b.priceIncludesTax,
        priceIncludesFees: b.priceIncludesFees,
        availabilityStatus: b.availabilityStatus || "AVAILABLE",
        city: b.city || "",
        department: b.department || "",
        country: b.country || "Colombia",
        effectiveDate: b.effectiveDate?.slice?.(0, 10) || b.effectiveDate,
        isCurrent: b.isCurrent,
        confidenceScore: b.confidenceScore ?? 80,
        notes: b.notes || "",
      });
      setFormOpen(true);
    } catch (err) {
      setToast(err.message || "Unable to load baseline.");
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const body = {
      vehicleSourceId: form.vehicleSourceId || undefined,
      sourceUrl: form.sourceUrl || undefined,
      brandDealerName: form.brandDealerName || undefined,
      make: form.make,
      model: form.model,
      trim: form.trim || undefined,
      version: form.version || undefined,
      modelYear: Number(form.modelYear),
      priceCop: Number(form.priceCop),
      currencyCode: form.currencyCode,
      priceType: form.priceType,
      priceIncludesTax: form.priceIncludesTax,
      priceIncludesFees: form.priceIncludesFees,
      availabilityStatus: form.availabilityStatus,
      city: form.city,
      department: form.department,
      country: form.country,
      effectiveDate: form.effectiveDate,
      isCurrent: form.isCurrent,
      confidenceScore: Number(form.confidenceScore),
      notes: form.notes || undefined,
    };
    try {
      if (editingId) {
        await updateNewPriceBaseline(editingId, body);
        setToast("Baseline updated.");
      } else {
        await createNewPriceBaseline(body);
        setToast("Baseline created.");
      }
      setFormOpen(false);
      load();
    } catch (err) {
      setToast(err.message || "Save failed.");
    }
  };

  const runAction = async (label, fn) => {
    try {
      await fn();
      setToast(label);
      load();
    } catch (err) {
      setToast(err.message || "Action failed.");
    }
  };

  return (
    <div className="admin-dashboard admin-vehicle-new-price-dash admin-vehicle-sources-dash--full-width">
      <header className="admin-dashboard__header">
        <div>
          <h1>New Vehicle Price Baselines</h1>
          <p className="muted">
            MSRP and dealer advertised new-vehicle prices for used-vs-new comparison (Mobility M22).
          </p>
        </div>
        <div className="admin-dashboard__header-actions">
          <button type="button" className="crm-btn crm-btn-secondary" onClick={openCreate}>
            Create baseline
          </button>
          <button
            type="button"
            className="crm-btn crm-btn-secondary"
            onClick={() => runAction("Recalculated used matches.", () => recalculateAllNewPriceMatches({ limit: 500 }))}
          >
            Recalculate all matches
          </button>
          {onClose ? (
            <button type="button" className="crm-btn crm-btn-secondary" onClick={onClose}>
              Close
            </button>
          ) : null}
        </div>
      </header>

      {toast ? <p className="admin-toast">{toast}</p> : null}
      {errorMessage ? <AdminStatePanel variant="error" message={errorMessage} /> : null}

      <div className="admin-filters-row">
        <label>
          Search
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Make, model, dealer…"
          />
        </label>
      </div>

      {loading ? (
        <AdminStatePanel message="Loading baselines…" />
      ) : baselines.length === 0 ? (
        <AdminStatePanel message="No baselines yet. Create one to enable used-vs-new comparisons." />
      ) : (
        <AdminTableContainer>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Make</th>
                <th>Model</th>
                <th>Trim</th>
                <th>Year</th>
                <th>Price</th>
                <th>Type</th>
                <th>Source</th>
                <th>Dealer</th>
                <th>City</th>
                <th>Dept</th>
                <th>Effective</th>
                <th>Observed</th>
                <th>Current?</th>
                <th>Conf.</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {baselines.map((row) => (
                <tr key={row.id}>
                  <td>{row.makeNormalized || row.makeRaw}</td>
                  <td>{row.modelNormalized || row.modelRaw}</td>
                  <td>{row.trimNormalized || row.trimRaw || "—"}</td>
                  <td>{row.modelYear}</td>
                  <td>{formatCop(row.priceCop)}</td>
                  <td>{row.priceType}</td>
                  <td>{row.vehicleSourceName || row.sourceName || "—"}</td>
                  <td>{row.brandDealerName || "—"}</td>
                  <td>{row.city || "—"}</td>
                  <td>{row.department || "—"}</td>
                  <td>{row.effectiveDate || "—"}</td>
                  <td>{formatDate(row.observedAt)}</td>
                  <td>{row.isCurrent ? "Yes" : "No"}</td>
                  <td>{row.confidenceScore ?? "—"}</td>
                  <td className="admin-table__actions">
                    <button type="button" className="admin-link-btn" onClick={() => openEdit(row.id)}>
                      Edit
                    </button>
                    {" · "}
                    <button
                      type="button"
                      className="admin-link-btn"
                      onClick={() =>
                        runAction("Marked current.", () => markNewPriceBaselineCurrent(row.id))
                      }
                    >
                      Mark current
                    </button>
                    {" · "}
                    <button
                      type="button"
                      className="admin-link-btn"
                      onClick={() => runAction("Expired.", () => expireNewPriceBaseline(row.id))}
                    >
                      Expire
                    </button>
                    {" · "}
                    <button
                      type="button"
                      className="admin-link-btn"
                      onClick={() =>
                        runAction("Matches recalculated.", () =>
                          recalculateBaselineNewPriceMatches(row.id)
                        )
                      }
                    >
                      Recalc matches
                    </button>
                    {row.sourceUrl ? (
                      <>
                        {" · "}
                        <a href={row.sourceUrl} target="_blank" rel="noreferrer">
                          Source
                        </a>
                      </>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </AdminTableContainer>
      )}

      {formOpen ? (
        <div className="admin-modal-backdrop" role="presentation" onClick={() => setFormOpen(false)}>
          <form
            className="admin-modal panel-card"
            onClick={(e) => e.stopPropagation()}
            onSubmit={handleSave}
          >
            <h2>{editingId ? "Edit baseline" : "Create baseline"}</h2>
            <div className="admin-form-grid">
              <label>
                Make *
                <input required value={form.make} onChange={(e) => setForm({ ...form, make: e.target.value })} />
              </label>
              <label>
                Model *
                <input required value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} />
              </label>
              <label>
                Trim
                <input value={form.trim} onChange={(e) => setForm({ ...form, trim: e.target.value })} />
              </label>
              <label>
                Model year *
                <input
                  type="number"
                  required
                  value={form.modelYear}
                  onChange={(e) => setForm({ ...form, modelYear: e.target.value })}
                />
              </label>
              <label>
                Price COP *
                <input
                  type="number"
                  required
                  min={1}
                  value={form.priceCop}
                  onChange={(e) => setForm({ ...form, priceCop: e.target.value })}
                />
              </label>
              <label>
                Price type
                <select value={form.priceType} onChange={(e) => setForm({ ...form, priceType: e.target.value })}>
                  <option value="MSRP">MSRP</option>
                  <option value="DEALER_ADVERTISED">Dealer advertised</option>
                  <option value="PROMOTIONAL">Promotional</option>
                  <option value="STARTING_FROM">Starting from</option>
                  <option value="CONFIGURED_PRICE">Configured</option>
                </select>
              </label>
              <label>
                Brand / dealer
                <input
                  value={form.brandDealerName}
                  onChange={(e) => setForm({ ...form, brandDealerName: e.target.value })}
                />
              </label>
              <label>
                Source URL
                <input value={form.sourceUrl} onChange={(e) => setForm({ ...form, sourceUrl: e.target.value })} />
              </label>
              <label>
                City
                <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
              </label>
              <label>
                Department
                <input
                  value={form.department}
                  onChange={(e) => setForm({ ...form, department: e.target.value })}
                />
              </label>
              <label>
                Effective date
                <input
                  type="date"
                  value={form.effectiveDate}
                  onChange={(e) => setForm({ ...form, effectiveDate: e.target.value })}
                />
              </label>
              <label>
                Confidence
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={form.confidenceScore}
                  onChange={(e) => setForm({ ...form, confidenceScore: e.target.value })}
                />
              </label>
              <label className="admin-form-checkbox">
                <input
                  type="checkbox"
                  checked={form.isCurrent}
                  onChange={(e) => setForm({ ...form, isCurrent: e.target.checked })}
                />
                Current baseline
              </label>
            </div>
            <label>
              Notes
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} />
            </label>
            <div className="admin-modal__actions">
              <button type="button" className="crm-btn crm-btn-secondary" onClick={() => setFormOpen(false)}>
                Cancel
              </button>
              <button type="submit" className="crm-btn crm-btn-primary">
                Save
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
};

export default VehicleNewPriceBaselinePage;
